/**
 * Unified storage utilities
 * Provides a single interface to read auth data from Redux (primary) or localStorage (fallback)
 * 
 * NOTE: These functions read from localStorage as the primary source since:
 * 1. Redux-persist stores the same data in localStorage under 'persist:root'
 * 2. Avoids circular dependencies with store imports
 * 3. Works in both React components and non-React code (API interceptors, etc.)
 * 
 * The data is still managed through Redux - these are just read helpers.
 */

/**
 * Get Redux-persisted state from localStorage
 * Redux-persist stores the entire state under 'persist:root'
 */
const getPersistedReduxState = (): any => {
  try {
    const persistedState = localStorage.getItem('persist:root');
    if (persistedState) {
      const parsed = JSON.parse(persistedState);
      if (parsed.auth) {
        const authState = JSON.parse(parsed.auth);
        return authState.globalState;
      }
    }
    return null;
  } catch (error) {
    console.error('Error reading persisted Redux state:', error);
    return null;
  }
};

/**
 * Get user permissions
 * Reads from Redux-persisted state first, falls back to direct localStorage
 */
export const getPermissions = (): string[] => {
  try {
    // Try Redux-persisted state first (source of truth)
    const reduxState = getPersistedReduxState();
    const reduxPermissions = reduxState?.user_permissions?.map((p: any) => p.name);
    
    if (reduxPermissions && reduxPermissions.length > 0) {
      return reduxPermissions;
    }

    // Fallback to direct localStorage (backward compatibility)
    const stored = localStorage.getItem('permissions');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error getting permissions:', error);
    return [];
  }
};

/**
 * Get tenant ID
 * Reads from Redux-persisted state first, falls back to direct localStorage
 */
export const getTenantId = (): number | null => {
  try {
    // Try Redux-persisted state first
    const reduxState = getPersistedReduxState();
    const reduxTenantId = reduxState?.tenant_id;
    
    if (reduxTenantId) {
      return reduxTenantId;
    }

    // Fallback to direct localStorage
    const stored = localStorage.getItem('tenant_id');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error getting tenant ID:', error);
    return null;
  }
};

/**
 * Get user branches
 * Reads from Redux-persisted state first, falls back to direct localStorage
 */
export const getBranches = (): number[] => {
  try {
    // Try Redux-persisted state first
    const reduxState = getPersistedReduxState();
    const reduxBranches = reduxState?.user_branches;
    
    if (reduxBranches) {
      // If it's a string, split it
      if (typeof reduxBranches === 'string') {
        return reduxBranches.split(',').map(Number).filter(Boolean);
      }
      return reduxBranches;
    }

    // Fallback to direct localStorage
    const stored = localStorage.getItem('branches');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error getting branches:', error);
    return [];
  }
};

/**
 * Get user groups
 * Reads from Redux-persisted state first, falls back to direct localStorage
 */
export const getUserGroups = (): any[] => {
  try {
    // Try Redux-persisted state first
    const reduxState = getPersistedReduxState();
    const reduxGroups = reduxState?.user_groups;
    
    if (reduxGroups && reduxGroups.length > 0) {
      return reduxGroups;
    }

    // Fallback to direct localStorage
    const stored = localStorage.getItem('user_groups');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error getting user groups:', error);
    return [];
  }
};

/**
 * Get access token
 * Reads from Redux-persisted state first, falls back to direct localStorage
 */
export const getAccessToken = (): string | null => {
  try {
    // Try Redux-persisted state first
    const reduxState = getPersistedReduxState();
    const reduxToken = reduxState?.access_token;
    
    if (reduxToken) {
      console.log('🔑 [STORAGE] Token found in Redux-persist');
      return reduxToken;
    }

    // Fallback to direct localStorage
    const fallbackToken = localStorage.getItem('token');
    if (fallbackToken) {
      console.log('🔑 [STORAGE] Token found in localStorage (fallback)');
      return fallbackToken;
    }
    
    console.warn('⚠️ [STORAGE] NO TOKEN FOUND in Redux-persist or localStorage!');
    console.warn('⚠️ [STORAGE] Redux state:', reduxState);
    console.warn('⚠️ [STORAGE] localStorage.token:', !!localStorage.getItem('token'));
    console.warn('⚠️ [STORAGE] localStorage[persist:root]:', !!localStorage.getItem('persist:root'));
    
    return null;
  } catch (error) {
    console.error('❌ [STORAGE] Error getting access token:', error);
    return null;
  }
};

